'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { LanguageToggle } from '@/components/language-toggle'
import { Locale, defaultLocale } from '@/i18n/routing'
import { NavItem } from '@/types/nav'
import Image from 'next/image'
import { Link, usePathname } from '@/i18n/navigation'
import { ArrowRight, ChevronDown } from 'lucide-react'

interface MobileNavProps {
  items?: NavItem[]
  locale: Locale
}

export function MobileNav({ items, locale }: MobileNavProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [expandedItem, setExpandedItem] = React.useState<string | null>(null)
  const navId = React.useId()
  const t = useTranslations('MobileNav')
  const linkLocale = locale === defaultLocale ? undefined : locale
  const pathname = usePathname()

  const isActive = React.useCallback(
    (href: string) => {
      if (!pathname) return false
      if (!href.startsWith('/')) return false
      if (href === '/') return pathname === '/'
      return pathname === href || pathname.startsWith(`${href}/`)
    },
    [pathname],
  )

  return (
    <>
      {/* 汉堡包菜单按钮 */}
      <Button
        variant="ghost"
        size="sm"
        className="h-9 w-9 p-0 md:hidden"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={navId}
      >
        <Icons.menu className="h-5 w-5" />
        <span className="sr-only">{t('toggle')}</span>
      </Button>

      {/* 背景遮罩 */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* 侧边栏 */}
      <div
        id={navId}
        inert={!isOpen}
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-80 max-w-full transform bg-background shadow-lg transition-transform duration-300 md:hidden',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex h-full flex-col">
          {/* 头部 */}
          <div className="flex items-center justify-between border-b p-4">
            <Image src="/logo.png" alt="logo" width={48} height={48} priority />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setIsOpen(false)}
            >
              <Icons.close className="h-4 w-4" />
              <span className="sr-only">{t('close')}</span>
            </Button>
          </div>

          {/* 导航菜单 */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {/* 现有的导航项 */}
              {items?.map((item) => {
                if (!item.href) return null
                const active = isActive(item.href)

                if (item.children?.length) {
                  const expanded = expandedItem === item.href
                  const submenuId = `${navId}-${item.href.replaceAll('/', '-')}`

                  return (
                    <div key={item.href}>
                      <button
                        type="button"
                        disabled={item.disabled}
                        aria-expanded={expanded}
                        aria-controls={submenuId}
                        onClick={() =>
                          setExpandedItem(expanded ? null : item.href!)
                        }
                        className={cn(
                          'flex w-full items-center justify-between rounded-sm py-2 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active
                            ? 'font-bold text-foreground'
                            : 'text-muted-foreground hover:text-primary',
                        )}
                      >
                        {item.title}
                        <ChevronDown
                          aria-hidden="true"
                          className={cn(
                            'h-4 w-4 transition-transform',
                            expanded && 'rotate-180',
                          )}
                        />
                      </button>
                      <div
                        id={submenuId}
                        hidden={!expanded}
                        className="mt-2 border-l pl-3"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            locale={linkLocale}
                            aria-current={
                              pathname === child.href ? 'page' : undefined
                            }
                            onClick={() => setIsOpen(false)}
                            className={cn(
                              'flex min-h-11 items-center rounded-sm px-3 py-3 text-sm leading-snug transition-colors hover:bg-accent hover:text-accent-foreground',
                              isActive(child.href)
                                ? 'bg-accent font-semibold text-foreground'
                                : 'text-muted-foreground',
                            )}
                          >
                            {child.title}
                          </Link>
                        ))}
                        <Link
                          href={item.href}
                          locale={linkLocale}
                          aria-current={
                            pathname === item.href ? 'page' : undefined
                          }
                          onClick={() => setIsOpen(false)}
                          className="mt-2 flex min-h-11 items-center justify-between gap-3 border-t px-3 py-3 text-sm font-medium text-primary hover:bg-accent"
                        >
                          {item.allItemsTitle ?? item.title}
                          <ArrowRight
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0"
                          />
                        </Link>
                      </div>
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    locale={linkLocale}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'block py-2 text-base font-medium transition-colors',
                      active
                        ? 'font-bold text-foreground'
                        : 'text-muted-foreground hover:text-primary',
                      item.disabled && 'cursor-not-allowed opacity-80',
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.title}
                  </Link>
                )
              })}
            </div>
          </nav>

          {/* 底部 - 语言切换 */}
          <div className="border-t p-4">
            <div className="flex items-center justify-center">
              <LanguageToggle locale={locale} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
